import { useFormik } from "formik"
import * as Yup from "yup"
import {
  Badge,
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap"
import { toast } from "react-toastify"

import InputField from "components/Common/InputField"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import ToggleButton from "components/Common/ToggleButton"
import { useUpdateNotificationEvent } from "hooks/notificationEventHook"

const PUSH_OPTIONS = [
  { value: "queued", label: "Queued (default)" },
  { value: "sync", label: "Sync (send immediately)" },
]

const validationSchema = Yup.object({
  title_en: Yup.string().trim().required("Title is required"),
  body_en: Yup.string().trim().required("Message is required"),
})

const NotificationEventModal = ({ isOpen, toggle, event }) => {
  const updateMutation = useUpdateNotificationEvent()

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title_en: event?.title_en ?? "",
      title_bn: event?.title_bn ?? "",
      body_en: event?.body_en ?? "",
      body_bn: event?.body_bn ?? "",
      route: event?.route ?? "",
      push_delivery: event?.push_delivery ?? "queued",
      deep_link: Boolean(event?.deep_link),
      active: event?.active ?? true,
    },
    validationSchema,
    onSubmit: async values => {
      try {
        await updateMutation.mutateAsync({
          id: event.id,
          title_en: values.title_en,
          title_bn: values.title_bn || null,
          body_en: values.body_en,
          body_bn: values.body_bn || null,
          route: values.route || null,
          push_delivery: values.push_delivery,
          deep_link: values.deep_link,
          active: values.active,
        })
        toast.success("Notification event updated")
        toggle()
      } catch (err) {
        toast.error(err?.response?.data?.message || "Save failed")
      }
    },
  })

  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    formik

  if (!event) return null

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable>
      <ModalHeader toggle={toggle}>Edit Notification</ModalHeader>
      <form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Badge color="light" className="text-dark">
              {event.key}
            </Badge>
            <Badge color="info">{event.group}</Badge>
            <Badge color="secondary">{event.permission}</Badge>
            {event.is_system && <Badge color="dark">System</Badge>}
          </div>

          <Row>
            <Col md={6}>
              <label className="form-label">Title (English) *</label>
              <InputField
                name="title_en"
                value={values.title_en}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={touched.title_en && !!errors.title_en}
              />
              {touched.title_en && errors.title_en && (
                <div className="text-danger small mb-2">{errors.title_en}</div>
              )}
            </Col>
            <Col md={6}>
              <label className="form-label">Title (Bangla)</label>
              <InputField
                name="title_bn"
                value={values.title_bn}
                onChange={handleChange}
                lang="bn"
              />
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <label className="form-label">Message (English) *</label>
              <InputField
                type="textarea"
                name="body_en"
                value={values.body_en}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={touched.body_en && !!errors.body_en}
              />
              {touched.body_en && errors.body_en && (
                <div className="text-danger small mb-2">{errors.body_en}</div>
              )}
            </Col>
            <Col md={6}>
              <label className="form-label">Message (Bangla)</label>
              <InputField
                type="textarea"
                name="body_bn"
                value={values.body_bn}
                onChange={handleChange}
                lang="bn"
              />
            </Col>
          </Row>

          <p className="text-muted small">
            Placeholders like <code>:actor</code>, <code>:label</code>,{" "}
            <code>:amount</code>, <code>:time</code> are filled in when the
            notification is sent.
          </p>

          <Row>
            <Col md={6}>
              <label className="form-label">In-app route</label>
              <InputField
                name="route"
                value={values.route}
                onChange={handleChange}
                placeholder="/vehicle/:id"
              />
            </Col>
            <Col md={6}>
              <FormikCreatableSelect
                name="push_delivery"
                formik={formik}
                label="Push delivery"
                options={PUSH_OPTIONS}
              />
            </Col>
          </Row>

          <div className="d-flex flex-wrap gap-2 mt-3">
            <ToggleButton
              active={values.active}
              color="success"
              onClick={() => setFieldValue("active", !values.active)}
            >
              {values.active ? "Active" : "Disabled"}
            </ToggleButton>
            <ToggleButton
              active={values.deep_link}
              color="primary"
              onClick={() => setFieldValue("deep_link", !values.deep_link)}
            >
              {values.deep_link
                ? "Opens route directly"
                : "Opens notification thread"}
            </ToggleButton>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" type="button" onClick={toggle}>
            Cancel
          </Button>
          <Button
            color="primary"
            type="submit"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}

export default NotificationEventModal
